import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX, FiSend, FiUser, FiMail, FiCheck,
  FiChevronDown, FiLoader, FiAlertCircle, FiCheckCircle,
} from 'react-icons/fi';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getBotResponse } from '../services/chatKnowledge';
import { sendEmail, buildVisitorEmail, buildOwnerEmail, OWNER_EMAIL } from '../services/emailService';

// ─── Constants ───────────────────────────────────────────────────────────────
const PURPOSE_OPTIONS = [
  { id: 'visit', label: 'Visit / Explore Portfolio' },
  { id: 'hire', label: 'Hire Full-time / Contract' },
  { id: 'career', label: 'Career / Internship Opportunity' },
  { id: 'freelance', label: 'Freelance / Consulting Project' },
  { id: 'app_dev', label: 'App Development (Mobile / Web)' },
  { id: 'project', label: 'Other Project Collaboration' },
];

const STAGES = {
  GREETING: 'greeting',   // Ask for name
  CHAT: 'chat',           // Free AI chat
  FORM: 'form',           // Lead form
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
};

// ─── Small helpers ────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

function BotMsg({ text, isHtml }) {
  return (
    <div className="flex gap-2.5 items-start">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5 shadow-sm">
        AI
      </div>
      <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%] shadow-sm">
        <p
          className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
          dangerouslySetInnerHTML={{
            __html: (text || '')
              .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
              .replace(/\n/g, '<br/>'),
          }}
        />
      </div>
    </div>
  );
}

function UserMsg({ text }) {
  return (
    <div className="flex gap-2.5 items-start justify-end">
      <div className="bg-blue-600 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%] shadow-sm">
        <p className="text-sm text-white leading-relaxed">{text}</p>
      </div>
      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
        <FiUser size={14} className="text-slate-600" />
      </div>
    </div>
  );
}

// ─── Lead Form ───────────────────────────────────────────────────────────────
function LeadForm({ visitorName, onBack, onSubmitSuccess }) {
  const [form, setForm] = useState({
    name: visitorName || '',
    email: '',
    purpose: [],
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const togglePurpose = (id) => {
    setForm((f) => ({
      ...f,
      purpose: f.purpose.includes(id)
        ? f.purpose.filter((p) => p !== id)
        : [...f.purpose, id],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (form.purpose.length === 0) e.purpose = 'Select at least one purpose';
    if (!form.subject.trim()) e.subject = 'Subject is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError('');

    const purposeLabels = form.purpose.map(
      (id) => PURPOSE_OPTIONS.find((o) => o.id === id)?.label || id
    );

    const timestamp = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

    const payload = {
      ...form,
      purposeLabels,
      timestamp,
      source: 'portfolio_chatbot',
    };

    try {
      // 1. Save to Firestore
      await addDoc(collection(db, 'portfolio_leads'), {
        ...payload,
        createdAt: serverTimestamp(),
      });

      // 2. Email to visitor
      await sendEmail({
        to: form.email,
        subject: `Thank you for reaching out, ${form.name}! — Kallesh D R`,
        text: buildVisitorEmail({ ...form, purpose: purposeLabels }),
      });

      // 3. Email to Kallesh
      await sendEmail({
        to: OWNER_EMAIL,
        subject: `New Portfolio Lead: ${form.name} — ${purposeLabels.join(', ')}`,
        text: buildOwnerEmail({ ...form, purpose: purposeLabels, timestamp }),
      });

      onSubmitSuccess(form.name);
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError('Something went wrong. Please try again or email kallesh953@gmail.com directly.');
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all';
  const labelCls = 'block text-xs font-semibold text-slate-600 mb-1';
  const errCls = 'text-xs text-red-500 mt-1';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-4 overflow-y-auto flex-1 min-h-0">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-1 h-5 bg-blue-600 rounded-full" />
        <p className="text-sm font-bold text-slate-800">Send Your Enquiry</p>
      </div>
      <p className="text-xs text-slate-500 -mt-2 mb-1">
        Fill in your details and Kallesh will get back within 24 hours. You'll receive a confirmation email.
      </p>

      {/* Name */}
      <div>
        <label className={labelCls} htmlFor="cf-name">Full Name *</label>
        <input
          id="cf-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Your full name"
          className={inputCls}
        />
        {errors.name && <p className={errCls}>{errors.name}</p>}
      </div>

      {/* Email */}
      <div>
        <label className={labelCls} htmlFor="cf-email">Email Address *</label>
        <input
          id="cf-email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="you@company.com"
          className={inputCls}
        />
        {errors.email && <p className={errCls}>{errors.email}</p>}
      </div>

      {/* Purpose Multi-Select */}
      <div>
        <label className={labelCls}>Purpose of Contact * (select all that apply)</label>
        <div className="grid grid-cols-1 gap-1.5">
          {PURPOSE_OPTIONS.map((opt) => {
            const selected = form.purpose.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => togglePurpose(opt.id)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all duration-150 text-left ${
                  selected
                    ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <div
                  className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                    selected ? 'bg-white border-white' : 'border-slate-300'
                  }`}
                >
                  {selected && <FiCheck size={10} className="text-blue-600" strokeWidth={3} />}
                </div>
                {opt.label}
              </button>
            );
          })}
        </div>
        {errors.purpose && <p className={errCls}>{errors.purpose}</p>}
      </div>

      {/* Subject */}
      <div>
        <label className={labelCls} htmlFor="cf-subject">Subject *</label>
        <input
          id="cf-subject"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="e.g. Senior SDE Role at Acme Corp"
          className={inputCls}
          autoComplete="off"
        />
        {errors.subject && <p className={errCls}>{errors.subject}</p>}
      </div>

      {/* Message */}
      <div>
        <label className={labelCls} htmlFor="cf-message">Message / Details</label>
        <textarea
          id="cf-message"
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Tell Kallesh about the role, project, or how you'd like to collaborate..."
          className={`${inputCls} resize-none`}
        />
      </div>

      {submitError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
          <FiAlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-600">{submitError}</p>
        </div>
      )}

      <div className="flex gap-2 mt-1">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 border border-slate-200 text-slate-600 text-sm font-medium py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
        >
          Back to Chat
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
        >
          {submitting ? (
            <>
              <FiLoader size={14} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <FiSend size={14} />
              Submit
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────
function SuccessScreen({ name, onClose }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 p-6 text-center gap-4">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200 }}
        className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center"
      >
        <FiCheckCircle size={32} className="text-green-500" />
      </motion.div>
      <div>
        <p className="font-bold text-slate-800 text-base">Enquiry Sent, {name}!</p>
        <p className="text-slate-500 text-xs mt-1.5 leading-relaxed">
          Your details have been saved and a confirmation has been sent to your email. Kallesh's copy has also been dispatched. Expect a reply within <strong>24 hours</strong>.
        </p>
      </div>
      <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 text-left w-full">
        <p className="text-xs font-semibold text-slate-600 mb-2">Kallesh's Direct Contacts</p>
        <p className="text-xs text-slate-500">📧 kallesh953@gmail.com</p>
        <p className="text-xs text-slate-500">📞 +91 7676654809</p>
        <p className="text-xs text-slate-500">💼 linkedin.com/in/kallesh-dr-087008235</p>
      </div>
      <button
        onClick={onClose}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors"
      >
        Close Chat
      </button>
    </div>
  );
}

// ─── Main Chatbot Component ───────────────────────────────────────────────────
export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState(STAGES.GREETING);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: "Hi! I'm AURA, Kallesh's AI assistant. I can answer anything about his skills, experience, projects, or availability.\n\nBefore we start — what's your name?",
    },
  ]);
  const [input, setInput] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unread, setUnread] = useState(1);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, stage]);

  const addBotMessage = useCallback((text, delay = 900) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [...prev, { role: 'bot', text }]);
      if (!open) setUnread((n) => n + 1);
    }, delay);
  }, [open]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setInput('');

    const userMsg = { role: 'user', text: trimmed };

    // Stage: Greeting — capture name
    if (stage === STAGES.GREETING) {
      const name = trimmed.split(' ')[0];
      setVisitorName(name);
      setMessages((prev) => [...prev, userMsg]);
      addBotMessage(
        `Nice to meet you, **${name}**! 👋\n\nI can tell you about Kallesh's skills, experience, projects, availability, and more. What would you like to know?\n\n(Say "contact" or "hire" anytime to send an enquiry form.)`
      );
      setStage(STAGES.CHAT);
      return;
    }

    // Stage: Chat — AI Q&A
    if (stage === STAGES.CHAT) {
      setMessages((prev) => [...prev, userMsg]);
      const response = getBotResponse(trimmed, visitorName);
      addBotMessage(response);

      // Detect if user wants to contact
      const lower = trimmed.toLowerCase();
      if (
        ['hire', 'contact', 'freelance', 'collaborate', 'project', 'job', 'recruit', 'reach'].some(
          (k) => lower.includes(k)
        )
      ) {
        setTimeout(() => {
          setStage(STAGES.FORM);
        }, 1200);
      }
      return;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Direct send for quick-prompt buttons (bypasses input state)
  const sendQuick = (text) => {
    setMessages((prev) => [...prev, { role: 'user', text }]);
    const response = getBotResponse(text, visitorName);
    addBotMessage(response);
    const lower = text.toLowerCase();
    if (['hire', 'contact', 'freelance', 'collaborate', 'project'].some((k) => lower.includes(k))) {
      setTimeout(() => setStage(STAGES.FORM), 1200);
    }
  };

  const openForm = () => setStage(STAGES.FORM);
  const backToChat = () => setStage(STAGES.CHAT);
  const handleSuccess = (name) => setStage(STAGES.SUCCESS);
  const handleClose = () => {
    setOpen(false);
    if (stage === STAGES.SUCCESS) {
      setTimeout(() => {
        setStage(STAGES.GREETING);
        setMessages([{
          role: 'bot',
          text: "Hi! I'm AURA, Kallesh's AI assistant. I can answer anything about his skills, experience, projects, or availability.\n\nBefore we start — what's your name?",
        }]);
        setVisitorName('');
        setUnread(0);
      }, 400);
    }
  };

  return (
    <>
      {/* ── Floating Trigger Button ── */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.92 }}
          onClick={() => setOpen((o) => !o)}
          id="chatbot-toggle"
          aria-label="Open AI Chatbot"
          className="relative flex items-center justify-center"
          style={{ width: 76, height: 76 }}
        >
          {/* Orbiting circular text — only shown when closed */}
          <AnimatePresence>
            {!open && (
              <motion.div
                key="orbit"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.7 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0 pointer-events-none"
                style={{ animation: 'orbitSpin 8s linear infinite' }}
              >
                <svg width="76" height="76" viewBox="0 0 76 76" fill="none">
                  <defs>
                    <path
                      id="orbitPath"
                      d="M 38,38 m -33,0 a 33,33 0 1,1 66,0 a 33,33 0 1,1 -66,0"
                    />
                  </defs>
                  <text
                    fill="#3b82f6"
                    fontSize="7.2"
                    fontWeight="700"
                    letterSpacing="1.8"
                    fontFamily="Inter, sans-serif"
                  >
                    <textPath href="#orbitPath" startOffset="0%">
                      ASK ME ANYTHING • ASK ME ANYTHING •
                    </textPath>
                  </text>
                </svg>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Core button circle */}
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-glow-blue flex items-center justify-center relative z-10">
            <AnimatePresence mode="wait">
              {open ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiX size={22} />
                </motion.div>
              ) : (
                <motion.div
                  key="robot"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Robot face SVG */}
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    {/* Head */}
                    <rect x="5" y="7" width="14" height="11" rx="2.5" fill="white" opacity="0.95"/>
                    {/* Eyes */}
                    <rect x="8" y="10" width="2.5" height="2.5" rx="0.6" fill="#3b82f6"/>
                    <rect x="13.5" y="10" width="2.5" height="2.5" rx="0.6" fill="#3b82f6"/>
                    {/* Mouth */}
                    <rect x="8.5" y="14" width="7" height="1.5" rx="0.75" fill="#93c5fd"/>
                    {/* Antenna */}
                    <rect x="11.25" y="4" width="1.5" height="3.2" rx="0.75" fill="white" opacity="0.9"/>
                    <circle cx="12" cy="3.5" r="1.2" fill="#93c5fd"/>
                    {/* Ears */}
                    <rect x="3" y="9.5" width="2" height="4" rx="1" fill="white" opacity="0.7"/>
                    <rect x="19" y="9.5" width="2" height="4" rx="1" fill="white" opacity="0.7"/>
                  </svg>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Unread badge */}
            {!open && unread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                {unread}
              </span>
            )}
          </div>
        </motion.button>
      </div>

      {/* ── Chat Window ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-slate-100 flex flex-col"
            style={{ height: '560px', maxHeight: 'calc(100vh - 110px)' }}
          >
            {/* ── Header ── */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4 flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white text-xs font-bold border border-white/30">
                AI
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm leading-tight">AURA AI</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-blue-100 text-xs">Online · Ask about Kallesh</span>
                </div>
              </div>
              {stage === STAGES.CHAT && (
                <button
                  onClick={openForm}
                  className="bg-white/20 hover:bg-white/30 border border-white/30 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center gap-1"
                >
                  <FiMail size={11} />
                  Contact
                </button>
              )}
              <button onClick={handleClose} className="text-white/70 hover:text-white ml-1" aria-label="Close chat">
                <FiX size={18} />
              </button>
            </div>

            {/* ── Body ── */}
            {stage === STAGES.FORM ? (
              <LeadForm visitorName={visitorName} onBack={backToChat} onSubmitSuccess={handleSuccess} />
            ) : stage === STAGES.SUCCESS ? (
              <SuccessScreen name={visitorName} onClose={handleClose} />
            ) : (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-slate-50/60">
                  {messages.map((msg, i) =>
                    msg.role === 'bot' ? (
                      <BotMsg key={i} text={msg.text} />
                    ) : (
                      <UserMsg key={i} text={msg.text} />
                    )
                  )}
                  {isTyping && (
                    <div className="flex gap-2.5 items-center">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        AI
                      </div>
                      <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm">
                        <TypingDots />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick prompts */}
                {stage === STAGES.CHAT && messages.length <= 3 && (
                  <div className="flex gap-1.5 px-4 py-2 overflow-x-auto scrollbar-hide border-t border-slate-100 bg-white flex-shrink-0">
                    {['His skills', 'Live projects', 'Hire him', 'Availability'].map((q) => (
                      <button
                        key={q}
                        onClick={() => sendQuick(q)}
                        className="flex-shrink-0 bg-blue-50 border border-blue-100 text-blue-700 text-[11px] font-semibold px-3 py-1.5 rounded-full hover:bg-blue-100 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                {/* Input */}
                <div className="flex-shrink-0 border-t border-slate-100 bg-white px-3 py-3">
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-2xl px-3 py-1.5 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                    <input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        stage === STAGES.GREETING
                          ? 'Type your name...'
                          : 'Ask about Kallesh...'
                      }
                      className="flex-1 bg-transparent text-sm text-slate-800 placeholder-slate-400 outline-none py-1"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!input.trim()}
                      aria-label="Send message"
                      className="w-8 h-8 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      <FiSend size={14} />
                    </button>
                  </div>
                  <p className="text-center text-[10px] text-slate-400 mt-1.5">
                    Powered by AURA AI • Only answers about his profile
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
